import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import { getMe, updateMe, changePassword } from '../services/api';

const TIMEZONES = [
  'Europe/Bratislava',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Warsaw',
  'Europe/Vienna',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const Settings = ({ setIsAuthenticated, darkMode, toggleDarkMode }) => {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [timezone, setTimezone] = useState('Europe/Bratislava');
  const [savingTimezone, setSavingTimezone] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getMe();
        if (res.success && res.admin) {
          setProfile(res.admin);
          setTimezone(res.admin.timezone || 'Europe/Bratislava');
        }
      } catch (err) {
        console.error('Load profile error:', err);
        toast.error('Failed to load profile');
      } finally {
        setLoadingProfile(false);
      }
    };
    load();
  }, []);

  const handleSaveTimezone = async (e) => {
    e.preventDefault();
    setSavingTimezone(true);
    try {
      const res = await updateMe({ timezone });
      if (res.success && res.admin) {
        setProfile(res.admin);
        setTimezone(res.admin.timezone);
        toast.success('Timezone saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save timezone');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Password change failed');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loadingProfile) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div className="spinner" />
      </Layout>
    );
  }

  return (
    <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="settings-page">
        <header className="workflows-page__header settings-page__header">
          <div>
            <h1 className="workflows-page__title">
              <MaterialIcon icon="settings" size={28} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Settings
            </h1>
            <p className="workflows-page__subtitle">
              Profile preferences and account security. Set your timezone and change your password here.
            </p>
          </div>
        </header>

        <div className="settings-grid-wrap">
          <section className="feature-card feature-card--start settings-section-card">
            <div className="feature-card__glow" />
            <div className="feature-card__content">
              <div className="settings-section-head">
                <span className="feature-card__icon-wrapper settings-section-icon settings-section-icon--theme">
                  <MaterialIcon icon="palette" size={24} />
                </span>
                <h3 className="settings-section-title">Appearance</h3>
              </div>
              <div className="settings-theme-options">
                <button
                  type="button"
                  className={`settings-theme-option ${!darkMode ? 'settings-theme-option--active' : ''}`}
                  onClick={() => darkMode && toggleDarkMode && toggleDarkMode()}
                >
                  <MaterialIcon icon="light_mode" size={20} />
                  Light
                </button>
                <button
                  type="button"
                  className={`settings-theme-option ${darkMode ? 'settings-theme-option--active' : ''}`}
                  onClick={() => !darkMode && toggleDarkMode && toggleDarkMode()}
                >
                  <MaterialIcon icon="dark_mode" size={20} />
                  Dark
                </button>
              </div>
            </div>
          </section>

          <section className="feature-card feature-card--start settings-section-card">
            <div className="feature-card__glow" />
            <div className="feature-card__content">
              <div className="settings-section-head">
                <span className="feature-card__icon-wrapper settings-section-icon">
                  <MaterialIcon icon="schedule" size={24} />
                </span>
                <h3 className="settings-section-title">Timezone</h3>
              </div>
              <form onSubmit={handleSaveTimezone} className="settings-section-form">
                <div className="form-group">
                  <label htmlFor="settings-timezone">Timezone</label>
                  <select
                    id="settings-timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="workflows-page__new-btn settings-section-form__submit" disabled={savingTimezone}>
                  {savingTimezone ? 'Saving...' : 'Save timezone'}
                </button>
              </form>
            </div>
          </section>

          <section className="feature-card feature-card--start settings-section-card">
            <div className="feature-card__glow" />
            <div className="feature-card__content">
              <div className="settings-section-head">
                <span className="feature-card__icon-wrapper settings-section-icon settings-section-icon--lock">
                  <MaterialIcon icon="lock" size={24} />
                </span>
                <h3 className="settings-section-title">Change password</h3>
              </div>
              <form onSubmit={handleChangePassword} className="settings-section-form">
                <div className="form-group">
                  <label htmlFor="settings-current-password">Current password</label>
                  <input
                    id="settings-current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="settings-new-password">New password</label>
                  <input
                    id="settings-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="settings-confirm-password">Confirm new password</label>
                  <input
                    id="settings-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    required
                    autoComplete="new-password"
                  />
                </div>
                <button type="submit" className="workflows-page__new-btn settings-section-form__submit" disabled={savingPassword}>
                  {savingPassword ? 'Changing password...' : 'Change password'}
                </button>
              </form>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
